import { useEffect, useMemo, useState } from 'react';
import { Alert, Checkbox, DatePicker, Select, Space, Spin, Table, Typography } from 'antd';
import dayjs from 'dayjs';
import dashboardApi from '../services/dashboardApi';
import type {
  MasterDashboardData,
  MasterDashboardNpiRow,
  MasterDashboardSummary,
  MasterDashboardTenantSummary,
} from '../types/masterDashboard.types';

const { Title, Paragraph, Text } = Typography;

function MasterOperationsDashboard() {
  const [dashboardData, setDashboardData] = useState<MasterDashboardData | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [billingMonth, setBillingMonth] = useState(() => dayjs().format('YYYY-MM'));
  const [selectedTenantSchemas, setSelectedTenantSchemas] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    const loadDashboard = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await dashboardApi.getMasterDashboardData(includeInactive, billingMonth);
        if (!active) {
          return;
        }
        setDashboardData(data);
      } catch (err) {
        if (!active) {
          return;
        }
        setError(err instanceof Error ? err.message : 'Unable to load master dashboard data.');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      active = false;
    };
  }, [billingMonth, includeInactive]);

  const insuredColumns = [
    { title: 'Tenant', dataIndex: 'tenant_name', key: 'tenant_name' },
    { title: 'Schema', dataIndex: 'tenant_schema', key: 'tenant_schema' },
    { title: 'Facility', dataIndex: 'facility_name', key: 'facility_name' },
    { title: 'Facility Code', dataIndex: 'facility_code', key: 'facility_code' },
    { title: 'Month', dataIndex: 'month_label', key: 'month_label' },
    { title: 'Insured Encounters', dataIndex: 'insured_encounters_total', key: 'insured_encounters_total' },
    { title: 'Days Counted', dataIndex: 'elapsed_days', key: 'elapsed_days' },
    { title: 'Avg / Day', dataIndex: 'avg_insured_per_day', key: 'avg_insured_per_day' },
    { title: 'Threshold', dataIndex: 'threshold', key: 'threshold' },
    {
      title: 'Status',
      key: 'status',
      render: (_: unknown, row: MasterDashboardSummary) => (
        <Text strong style={{ color: row.above_threshold ? '#c43d2f' : '#3c763d' }}>
          {row.above_threshold ? 'Above threshold' : 'Discount Qualified'}
        </Text>
      ),
    },
  ];

  const compareText = (left: string | null | undefined, right: string | null | undefined) =>
    (left || '').localeCompare(right || '', undefined, { sensitivity: 'base' });

  const compareNumber = (left: number | null | undefined, right: number | null | undefined) =>
    (left || 0) - (right || 0);

  const tenantSummaryColumns = [
    {
      title: 'Tenant',
      dataIndex: 'tenant_name',
      key: 'tenant_name',
      sorter: (left: MasterDashboardTenantSummary, right: MasterDashboardTenantSummary) =>
        compareText(left.tenant_name, right.tenant_name),
    },
    {
      title: 'Schema',
      dataIndex: 'tenant_schema',
      key: 'tenant_schema',
      sorter: (left: MasterDashboardTenantSummary, right: MasterDashboardTenantSummary) =>
        compareText(left.tenant_schema, right.tenant_schema),
    },
    {
      title: 'Total NPI Users',
      dataIndex: 'npi_user_count',
      key: 'npi_user_count',
      sorter: (left: MasterDashboardTenantSummary, right: MasterDashboardTenantSummary) =>
        compareNumber(left.npi_user_count, right.npi_user_count),
    },
    {
      title: 'Active NPI Users',
      dataIndex: 'active_npi_user_count',
      key: 'active_npi_user_count',
      sorter: (left: MasterDashboardTenantSummary, right: MasterDashboardTenantSummary) =>
        compareNumber(left.active_npi_user_count, right.active_npi_user_count),
    },
  ];

  const npiColumns = [
    {
      title: 'Tenant',
      dataIndex: 'tenant_name',
      key: 'tenant_name',
      sorter: (left: MasterDashboardNpiRow, right: MasterDashboardNpiRow) =>
        compareText(left.tenant_name, right.tenant_name),
    },
    {
      title: 'Employee',
      dataIndex: 'employee_name',
      key: 'employee_name',
      sorter: (left: MasterDashboardNpiRow, right: MasterDashboardNpiRow) =>
        compareText(left.employee_name, right.employee_name),
    },
    {
      title: 'NPI',
      dataIndex: 'npi',
      key: 'npi',
      sorter: (left: MasterDashboardNpiRow, right: MasterDashboardNpiRow) =>
        compareText(left.npi, right.npi),
    },
    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      sorter: (left: MasterDashboardNpiRow, right: MasterDashboardNpiRow) =>
        Number(left.active) - Number(right.active),
      render: (value: boolean) => (value ? 'Yes' : 'No'),
    },
    {
      title: 'Job',
      dataIndex: 'job_name',
      key: 'job_name',
      sorter: (left: MasterDashboardNpiRow, right: MasterDashboardNpiRow) =>
        compareText(left.job_name, right.job_name),
    },
    {
      title: 'Billing ID',
      dataIndex: 'billing_id',
      key: 'billing_id',
      sorter: (left: MasterDashboardNpiRow, right: MasterDashboardNpiRow) =>
        compareText(left.billing_id, right.billing_id),
    },
    {
      title: 'Termination Date',
      dataIndex: 'termination_date',
      key: 'termination_date',
      sorter: (left: MasterDashboardNpiRow, right: MasterDashboardNpiRow) =>
        compareText(left.termination_date, right.termination_date),
      render: (value: string | null) => {
        if (!value) {
          return '';
        }
        return new Date(value).toLocaleDateString();
      },
    },
  ];

  const tenantFilterOptions = useMemo(() => {
    if (!dashboardData) {
      return [];
    }

    return dashboardData.tenant_summaries.map((tenant) => ({
      label: tenant.tenant_code
        ? `${tenant.tenant_name} (${tenant.tenant_code})`
        : tenant.tenant_name,
      value: tenant.tenant_schema,
    }));
  }, [dashboardData]);

  const filteredNpiRows = useMemo(() => {
    if (!dashboardData) {
      return [];
    }
    if (selectedTenantSchemas.length === 0) {
      return dashboardData.npi_rows;
    }
    const selectedSchemas = new Set(selectedTenantSchemas);
    return dashboardData.npi_rows.filter((row) => selectedSchemas.has(row.tenant_schema));
  }, [dashboardData, selectedTenantSchemas]);

  const denominatorText = useMemo(() => {
    if (!dashboardData) {
      return '';
    }

    const dayCounts = Array.from(new Set(
      dashboardData.insured_daily_summaries.map((summary) => summary.elapsed_days),
    )).sort((left, right) => left - right);

    if (dayCounts.length === 0) {
      return 'No facility billing rows are available for this period.';
    }
    if (dayCounts.length === 1) {
      return `${dayCounts[0]} calendar day${dayCounts[0] === 1 ? '' : 's'} counted.`;
    }
    return `${dayCounts[0]}-${dayCounts[dayCounts.length - 1]} facility-local calendar days counted.`;
  }, [dashboardData]);

  if (loading && !dashboardData) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <Spin size="large" tip="Loading master dashboard..." />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <Alert
          message="Master Dashboard Error"
          description={error}
          type="error"
          showIcon
        />
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div style={{ padding: '20px' }}>
        <Alert
          message="No Master Dashboard Data"
          description="Unable to load master dashboard configuration."
          type="warning"
          showIcon
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={2} style={{ marginBottom: 8 }}>Master Dashboard</Title>
          <Paragraph style={{ marginBottom: 16 }}>
            Cross-tenant operational reporting from the <strong>{dashboardData.schema_name}</strong> master schema.
          </Paragraph>
          <Checkbox
            checked={includeInactive}
            onChange={(event) => setIncludeInactive(event.target.checked)}
          >
            Include inactive users
          </Checkbox>
        </div>

        <div>
          <Title level={4} style={{ marginBottom: 8 }}>Insured Encounters / Day (Billing Month)</Title>
          <Paragraph type="secondary">
            Pricing threshold is editable in Settings and applies to the average insured encounter count per calendar day for each facility.
          </Paragraph>
          <Space direction="vertical" size={4} style={{ marginBottom: 16 }}>
            <label htmlFor="ops-billing-month"><Text strong>Billing Month</Text></label>
            <DatePicker
              id="ops-billing-month"
              picker="month"
              allowClear={false}
              value={dayjs(`${billingMonth}-01`)}
              format="MMMM YYYY"
              disabled={loading}
              disabledDate={(current) => current.startOf('month').isAfter(dayjs().startOf('month'))}
              onChange={(value) => {
                if (value) {
                  setBillingMonth(value.format('YYYY-MM'));
                }
              }}
            />
            <Text type="secondary">
              {dashboardData.billing_period.month_label}: {denominatorText}
            </Text>
          </Space>
          <Table<MasterDashboardSummary>
            rowKey={(row) => `${row.tenant_schema}-${row.facility_id ?? 'unassigned'}`}
            columns={insuredColumns}
            dataSource={dashboardData.insured_daily_summaries}
            loading={loading}
            pagination={false}
            size="middle"
            scroll={{ x: 1100 }}
          />
        </div>

        <div>
          <Title level={4} style={{ marginBottom: 8 }}>NPI Users by Tenant</Title>
          <Table<MasterDashboardTenantSummary>
            rowKey={(row) => row.tenant_schema}
            columns={tenantSummaryColumns}
            dataSource={dashboardData.tenant_summaries}
            pagination={false}
            size="middle"
            scroll={{ x: 700 }}
          />
        </div>

        <div>
          <Title level={4} style={{ marginBottom: 8 }}>NPI User Detail</Title>
          <Space direction="vertical" size="small" style={{ width: '100%', marginBottom: 16 }}>
            <Typography.Text type="secondary">
              Filter e-prescriber detail by tenant to review client-specific billing counts.
            </Typography.Text>
            <Select
              mode="multiple"
              allowClear
              placeholder="Filter tenants"
              value={selectedTenantSchemas}
              onChange={setSelectedTenantSchemas}
              options={tenantFilterOptions}
              style={{ width: '100%', maxWidth: 560 }}
              optionFilterProp="label"
            />
          </Space>
          <Table<MasterDashboardNpiRow>
            rowKey={(row) => `${row.tenant_schema}-${row.employee_id}-${row.billing_id}`}
            columns={npiColumns}
            dataSource={filteredNpiRows}
            pagination={{ pageSize: 25, showSizeChanger: false }}
            size="middle"
            scroll={{ x: 1100 }}
          />
        </div>
      </Space>
    </div>
  );
}

export default MasterOperationsDashboard;
